import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

const LAST_UPDATED = "June 23, 2026";

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="sticky top-0 z-10 border-b border-neutral-100 bg-neutral-50/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <button
            aria-label="Go back"
            className="flex h-6 w-6 items-center justify-center text-neutral-300"
            onClick={() => navigate(-1)}
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </button>
          <h5 className="font-semibold text-neutral-300">Privacy Policy</h5>
          <span className="h-6 w-6" />
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-5 pb-16 pt-6">
        <p className="mb-6 text-center text-sm text-neutral-200">
          Oya! Fuel — Last updated: {LAST_UPDATED}
        </p>

        <div className="space-y-6 text-sm leading-relaxed text-neutral-300 md:text-base">
          <p>
            Kost Konsult Ltd (&quot;Oya!&quot;, &quot;we&quot;, &quot;us&quot;,
            or &quot;our&quot;) operates the Oya! Fuel mobile application and
            website (together, &quot;the Service&quot;). This Privacy Policy
            explains how we collect, use, store, and protect your personal
            information when you use the Service to buy fuel, hold and share fuel
            coupons, redeem them at partner stations, and manage your wallet.
          </p>
          <p>
            By using Oya! Fuel, you agree to the collection and use of
            information as described in this policy.
          </p>

          <Section title="1. Information We Collect">
            <h4 className="pt-2 font-semibold text-neutral-300">
              1.1 Account Information
            </h4>
            <p>When you create an account or log in, we collect:</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>Phone number and country code</li>
              <li>Password (securely hashed, never stored in plain text)</li>
              <li>First and last name</li>
              <li>Email address (if you provide one)</li>
              <li>Profile photo (if you provide one)</li>
            </ul>

            <h4 className="pt-2 font-semibold text-neutral-300">
              1.2 Fuel Purchase and Coupon Details
            </h4>
            <p>
              When you buy fuel for yourself or for someone else, or transfer or
              share a coupon, we collect:
            </p>
            <ul className="list-disc space-y-1 pl-5">
              <li>The fuel product and the amount or value you purchase</li>
              <li>
                The name and phone number of the beneficiary or recipient when
                you buy fuel for, or share a coupon with, another person
              </li>
              <li>The fuel station, branch, or region you select</li>
              <li>Coupon codes and redemption records</li>
            </ul>

            <h4 className="pt-2 font-semibold text-neutral-300">
              1.3 Wallet and Transaction Information
            </h4>
            <p>
              We keep a record of your wallet balance, top-ups, and your
              purchase, transfer, redemption, and repayment history so you can
              view and manage your activity.
            </p>

            <h4 className="pt-2 font-semibold text-neutral-300">
              1.4 Payment Information
            </h4>
            <p>
              To pay with Mobile Money, we collect your mobile money account
              name, account number, and network provider, and process a one-time
              authorization code (OTP) to confirm the transaction. Payments are
              processed through our payment provider and the relevant mobile
              money networks. We do <strong>not</strong> store your Mobile Money
              PIN or wallet password. If you have an account, you may save a
              default payment account for faster checkout.
            </p>

            <h4 className="pt-2 font-semibold text-neutral-300">
              1.5 Device and Usage Data
            </h4>
            <p>
              We may collect basic device and browser information (such as
              device model, operating system or browser version, and unique
              identifiers) and information about how you interact with the
              Service — including purchases, transfers, timestamps, and error
              logs — to operate, secure, and improve the Service. The Service
              does <strong>not</strong> track your device&apos;s GPS location.
            </p>

            <h4 className="pt-2 font-semibold text-neutral-300">
              1.6 Cookies and Local Storage
            </h4>
            <p>
              We use essential local storage and cookies to keep you signed in
              (for example, to store your authentication token) and to make the
              Service work. We do not use cookies for third-party advertising.
            </p>
          </Section>

          <Section title="2. How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                Create and secure your account and verify your identity via OTP
              </li>
              <li>Process fuel purchases and issue and manage your coupons</li>
              <li>Let you transfer or share coupons with other people</li>
              <li>Process Mobile Money payments and confirm transactions</li>
              <li>Allow you to redeem coupons at partner fuel stations</li>
              <li>Maintain your wallet balance and transaction history</li>
              <li>
                Send transaction confirmations, coupon updates, and support
                messages by SMS or notification
              </li>
              <li>Provide customer support and respond to your enquiries</li>
              <li>
                Improve app and website performance and fix technical issues
              </li>
              <li>Comply with applicable laws and regulations</li>
            </ul>
          </Section>

          <Section title="3. Information Sharing">
            <p>
              We share your information only as needed to provide the Service:
            </p>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                <strong>Partner fuel stations and branches</strong>, to verify
                and redeem the coupons you present
              </li>
              <li>
                <strong>Coupon recipients</strong>, who will see the coupon you
                transfer or share with them
              </li>
              <li>
                <strong>Payment provider and mobile money networks</strong>, to
                process and confirm your payments
              </li>
              <li>
                <strong>Service providers</strong> (such as SMS and hosting
                partners) that help us operate the Service
              </li>
              <li>
                <strong>Authorities</strong>, when required by law or to protect
                safety
              </li>
            </ul>
            <p>
              We do <strong>not</strong> sell your personal data, and we do not
              share it with third parties for advertising or marketing purposes.
            </p>
          </Section>

          <Section title="4. Data Storage and Security">
            <ul className="list-disc space-y-1 pl-5">
              <li>Your authentication token is stored securely on your device</li>
              <li>
                We use encrypted HTTPS connections for all data transmitted
                between the Service and our servers
              </li>
              <li>
                Passwords are securely hashed and never stored or transmitted in
                plain text
              </li>
              <li>
                Your Mobile Money PIN or wallet credentials are never stored by
                us
              </li>
              <li>
                We implement industry-standard security measures to protect your
                data against unauthorized access
              </li>
            </ul>
          </Section>

          <Section title="5. Data Retention">
            <ul className="list-disc space-y-1 pl-5">
              <li>
                Purchase, coupon, and payment records are retained for
                operational, financial, and legal or tax purposes
              </li>
              <li>
                Account information is retained for as long as your account
                remains active
              </li>
              <li>
                If you delete your account, your personal data will be deleted or
                anonymized within 90 days, except where retention is required by
                law
              </li>
            </ul>
          </Section>

          <Section title="6. Your Rights">
            <p>You have the right to:</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>Access the personal data we hold about you</li>
              <li>
                Request correction of inaccurate data, or update your profile in
                the app
              </li>
              <li>
                Delete your account and the data associated with it from the
                Settings screen (subject to legal and operational requirements)
              </li>
              <li>Withdraw consent or close your account</li>
            </ul>
            <p>
              To exercise any of these rights, contact us at the details provided
              below.
            </p>
          </Section>

          <Section title="7. Children's Privacy">
            <p>
              Oya! Fuel is intended for use by adults aged 18 and over. We do not
              knowingly collect personal information from children.
            </p>
          </Section>

          <Section title="8. Third-Party Services">
            <p>The Service uses the following third-party services:</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                <strong>Mobile money networks and our payment provider</strong> —
                for processing payments (subject to their respective privacy
                policies)
              </li>
              <li>
                <strong>SMS provider</strong> — for sending one-time codes and
                transaction notifications
              </li>
            </ul>
          </Section>

          <Section title="9. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. We will notify
              you of significant changes through the Service or other appropriate
              channels. Continued use of the Service after changes constitutes
              acceptance of the updated policy.
            </p>
          </Section>

          <Section title="10. Contact Us">
            <p>
              If you have questions about this Privacy Policy or your personal
              data, contact us at:
            </p>
            <p className="leading-7">
              <strong>Kost Konsult Ltd</strong>
              <br />
              Email:{" "}
              <a
                className="text-primary underline"
                href="mailto:info@oyaghana.com"
              >
                info@oyaghana.com
              </a>
              <br />
              Phone:{" "}
              <a className="text-primary underline" href="tel:+233594107075">
                059 410 7075
              </a>{" "}
              /{" "}
              <a className="text-primary underline" href="tel:+233208659922">
                020 865 9922
              </a>
              <br />
              Website:{" "}
              <a
                className="text-primary underline"
                href="https://oyaghana.com"
                target="_blank"
                rel="noreferrer"
              >
                oyaghana.com
              </a>
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className="space-y-3">
    <h3 className="text-base font-bold text-neutral-300">{title}</h3>
    {children}
  </section>
);

export default Privacy;
